import { useBrand } from '@/config/brands';
import { type FraudCategory } from '@/config/fraudCategories';
import { type HospitalOutlier } from '@/hooks/useFraudCategoryPricing';
import { FraudRiskBadge } from './FraudRiskBadge';
import { markupBadgeColor } from '@/lib/grades';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExternalLink, Shield, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Patriotic palette
const NAVY = '#002868';
const RED = '#B22234';
const GOLD = '#C5A55A';
const CREAM = '#FAF9F6';

interface HospitalInvestigationModalProps {
  isOpen: boolean;
  onClose: () => void;
  outlier: HospitalOutlier | null;
  category: FraudCategory | null;
}

const fmt = (v: number | null | undefined) => {
  if (v == null || isNaN(v)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(v);
};

const fmtPct = (v: number | null | undefined) => {
  if (v == null) return '—';
  const sign = v >= 0 ? '+' : '';
  if (Math.abs(v) >= 1000) return `${sign}${(v / 1000).toFixed(1)}k%`;
  return `${sign}${v.toFixed(1)}%`;
};

export const HospitalInvestigationModal = ({
  isOpen,
  onClose,
  outlier,
  category,
}: HospitalInvestigationModalProps) => {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const navigate = useNavigate();

  if (!outlier || !category) return null;

  // Sort pricing by markup descending
  const sortedPricing = [...outlier.codePricing].sort(
    (a, b) => b.grossMarkupPercentage - a.grossMarkupPercentage
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        style={isAhp ? {
          backgroundColor: CREAM,
          border: `1px solid rgba(0,40,104,0.15)`,
        } : undefined}
      >
        {/* Header with patriotic navy banner */}
        {isAhp ? (
          <div
            className="-mx-6 -mt-6 mb-4 p-5 relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${NAVY} 0%, #001a4a 100%)`,
              borderBottom: `2px solid ${GOLD}`,
            }}
          >
            {/* Decorative stars */}
            <div className="absolute top-2 right-4 flex gap-1.5 opacity-25">
              {[...Array(3)].map((_, i) => (
                <Star key={i} className="w-3 h-3" style={{ color: GOLD, fill: GOLD }} />
              ))}
            </div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <Shield className="w-5 h-5" style={{ color: CREAM }} />
              </div>
              <div>
                <h2
                  className="text-lg font-bold"
                  style={{ color: CREAM, fontFamily: "'Instrument Serif', Georgia, serif" }}
                >
                  {outlier.hospitalName}
                </h2>
                <p className="text-xs" style={{ color: 'rgba(250,249,246,0.6)' }}>
                  {outlier.city} &middot; {category.shortName}
                </p>
              </div>
              <div className="ml-auto">
                <FraudRiskBadge level={outlier.riskLevel} />
              </div>
            </div>
            <div className="flex items-center gap-4 mt-2">
              <span className="text-xs font-medium" style={{ color: GOLD }}>
                Avg Markup:{' '}
                <span className="font-bold">{fmtPct(outlier.avgMarkup)}</span>
              </span>
              <span className="text-xs font-bold" style={{ color: RED }}>
                Max Markup: {fmtPct(outlier.maxMarkup)}
              </span>
              <span className="text-xs" style={{ color: 'rgba(250,249,246,0.5)' }}>
                {outlier.codeCount} codes with data
              </span>
            </div>
            {/* Gold accent line */}
            <div
              className="absolute bottom-0 left-0 right-0 h-0.5"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}
            />
          </div>
        ) : (
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-3">
              {outlier.hospitalName}
              <FraudRiskBadge level={outlier.riskLevel} />
            </DialogTitle>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {outlier.city} &middot; Category: <span className="font-medium">{category.shortName}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Avg Markup:{' '}
                <span className="font-bold" style={{ color: markupBadgeColor(outlier.avgMarkup) }}>
                  {fmtPct(outlier.avgMarkup)}
                </span>
                {' '}&middot;{' '}
                Max Markup:{' '}
                <span className="font-bold" style={{ color: markupBadgeColor(outlier.maxMarkup) }}>
                  {fmtPct(outlier.maxMarkup)}
                </span>
                {' '}&middot;{' '}
                {outlier.codeCount} codes with data
              </p>
            </div>
          </DialogHeader>
        )}

        <div className={isAhp ? '' : 'mt-4'}>
          {/* Risk indicators */}
          {category.riskIndicators.length > 0 && (
            <div
              className="mb-4 p-3.5 rounded-xl text-xs"
              style={isAhp ? {
                background: `linear-gradient(135deg, rgba(0,40,104,0.05) 0%, rgba(0,40,104,0.02) 100%)`,
                border: `1px solid ${NAVY}15`,
              } : {
                backgroundColor: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.2)',
              }}
            >
              <p
                className="font-semibold mb-1.5"
                style={isAhp ? {
                  color: NAVY,
                  fontFamily: "'Instrument Serif', Georgia, serif",
                  fontSize: '0.8rem',
                } : { color: '#fbbf24' }}
              >
                Common fraud indicators for {category.shortName}:
              </p>
              <ul className="list-disc list-inside space-y-0.5" style={{ color: isAhp ? 'rgba(27,27,27,0.6)' : '#fcd34d' }}>
                {category.riskIndicators.map((ri, i) => (
                  <li key={i}>{ri}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Code-by-code breakdown table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  style={isAhp ? {
                    background: `linear-gradient(135deg, ${NAVY} 0%, #001a4a 100%)`,
                  } : undefined}
                  className={!isAhp ? 'border-b bg-muted/50' : ''}
                >
                  {['Code', 'Description', 'Hospital Charge', 'National Avg', 'Markup %', 'Setting'].map((label, i) => (
                    <th
                      key={label}
                      className={`p-2.5 font-semibold text-xs tracking-wide uppercase ${
                        i >= 2 ? (i === 5 ? 'text-center' : 'text-right') : 'text-left'
                      }`}
                      style={isAhp ? { color: CREAM } : undefined}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedPricing.map((p, idx) => {
                  const isExtreme = p.grossMarkupPercentage > 500;
                  return (
                    <tr
                      key={idx}
                      style={isAhp ? {
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        backgroundColor: isExtreme
                          ? 'rgba(178,34,52,0.06)'
                          : idx % 2 === 0 ? 'transparent' : 'rgba(0,40,104,0.02)',
                      } : {
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        backgroundColor: isExtreme ? 'rgba(239,68,68,0.08)' : undefined,
                      }}
                    >
                      <td
                        className="p-2.5 font-mono text-xs"
                        style={isAhp ? { color: NAVY, fontWeight: 600 } : undefined}
                      >
                        {p.procedureCode}
                      </td>
                      <td
                        className="p-2.5 text-xs max-w-sm truncate"
                        title={p.procedureDescription}
                        style={isAhp ? { color: 'rgba(27,27,27,0.7)' } : undefined}
                      >
                        {p.procedureDescription}
                      </td>
                      <td
                        className="p-2.5 text-right font-mono text-xs"
                        style={isAhp ? { color: '#1B1B1B' } : undefined}
                      >
                        {fmt(p.hospitalGrossCharge)}
                      </td>
                      <td
                        className="p-2.5 text-right font-mono text-xs"
                        style={isAhp ? { color: 'rgba(27,27,27,0.62)' } : undefined}
                      >
                        {fmt(p.nationalAvgGross)}
                      </td>
                      <td className="p-2.5 text-right font-mono text-xs font-bold">
                        <span style={{ color: markupBadgeColor(p.grossMarkupPercentage) }}>
                          {fmtPct(p.grossMarkupPercentage)}
                        </span>
                      </td>
                      <td
                        className="p-2.5 text-center text-xs"
                        style={isAhp ? { color: 'rgba(27,27,27,0.4)' } : undefined}
                      >
                        {p.inpatientOutpatient || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer link */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => {
                onClose();
                navigate(`/hospital/${outlier.hospitalId}`);
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={isAhp ? {
                background: `linear-gradient(135deg, ${NAVY} 0%, #001a4a 100%)`,
                color: CREAM,
                border: `1px solid ${GOLD}40`,
                boxShadow: '0 2px 8px rgba(0,40,104,0.2)',
              } : {
                backgroundColor: 'rgba(0,255,65,0.15)',
                color: '#00ff41',
                border: '1px solid rgba(0,255,65,0.3)',
              }}
            >
              View Full Hospital Profile
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
